import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'
import DesktopAssetAllocation from './DesktopAssetAllocation'
import MobileAssetAllocation from './MobileAssetAllocation'

/**
 * Asset allocation display for the step-by-step advice flow's investments step.
 *
 * Shows each asset class (and, at the investment level, the holdings within it)
 * with the current portfolio in grey and the plan portfolio in the
 * client's brand blue. Chooses its presentation responsively:
 *  - desktop: paired grey/blue bars split by a vertical rule (borrows the
 *    current asset allocation layout).
 *  - mobile:  stacked grey/blue bars sized within a fixed track so they never
 *    run into the value labels, with an expand/collapse caret per category to
 *    reveal the investment-level breakdown.
 *
 * `level` selects the granularity:
 *  - 'overall':    just the top-level asset classes (Stocks, Bonds, Cash, Misc).
 *  - 'investment': each asset class plus its individual holdings.
 */
const AdviceAssetAllocation = props => {
  const { level = 'overall', ...rest } = props
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  return isMobile ? (
    <MobileAssetAllocation level={level} {...rest} />
  ) : (
    <DesktopAssetAllocation level={level} {...rest} />
  )
}

const valueShape = PropTypes.shape({
  /** Percentage of the portfolio (0–100). */
  percent: PropTypes.number,
  /** Dollar amount. */
  dollar: PropTypes.number,
})

AdviceAssetAllocation.propTypes = {
  /**
   * Asset classes to display. Each has a current and recommended value and,
   * optionally, the individual investments within it (shown at the investment
   * level / when a mobile category is expanded).
   */
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      current: valueShape,
      recommended: valueShape,
      investments: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          current: valueShape,
          recommended: valueShape,
        })
      ),
    })
  ).isRequired,
  /** Granularity of the breakdown. */
  level: PropTypes.oneOf(['overall', 'investment']),
  /**
   * The account has no current balance, so the recommended mix is shown as
   * percentages only (no "$0" amounts), with a note and an "account has no
   * balance" label in place of the empty current side.
   */
  zeroBalance: PropTypes.bool,
  /** Heading shown above the mobile cards (ignored on desktop). */
  title: PropTypes.string,
  /** Relabels the plan side as "Your changes" when displaying a modified case. */
  isModified: PropTypes.bool,
  /** Mobile only: let each card expand to reveal its holdings (account views). */
  expandable: PropTypes.bool,
  className: PropTypes.string,
}

export default AdviceAssetAllocation
